import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGrantInternalOrganizationAccessToAllMutation, useSetInternalOrganizationAccessMutation } from '../../../api/permissions'
import { useMutationFeedback } from '../../../hooks/useMutationFeedback'
import { InternalPermissionType } from '../../../types/InternalPermission'
import { getMissingGrants } from '../components/helpers/organizationAccessTabHelpers'

type AccessItem = { id: string; name: string }

/**
 * Grants and revokes for one organization, each checked against the access read back after the write. The writes run one at a time,
 * so a read-back can't include another change still in flight and a grant to all can't interleave with single changes.
 */
export const useOrganizationAccessChange = (organization: { id: string; name: string }, type: InternalPermissionType) => {
  const { t } = useTranslation()
  const { notify, run } = useMutationFeedback()
  const [setAccess] = useSetInternalOrganizationAccessMutation()
  const [grantAll] = useGrantInternalOrganizationAccessToAllMutation()
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set())
  const [isGrantingAll, setIsGrantingAll] = useState(false)
  const queue = useRef<Promise<void>>(Promise.resolve())

  const enqueue = useCallback((write: () => Promise<void>) => {
    const queued = queue.current.then(write)
    queue.current = queued.catch(() => undefined)
    return queued
  }, [])

  const failureMessages = useCallback(
    (key: string, options: Record<string, unknown>) => ({ error: t(key, options), errorWithReason: (reason: string) => `${t(key, options)}: ${reason}` }),
    [t]
  )

  const changeAccess = useCallback(
    async (item: AccessItem, granted: boolean) => {
      const options = { name: item.name, organization: organization.name }
      setPendingIds((current) => new Set(current).add(item.id))
      await enqueue(async () => {
        try {
          const saved = await run(
            setAccess({ organizationId: organization.id, type, itemId: item.id, granted }).unwrap(),
            failureMessages(granted ? 'internal-csm:organization_access_grant_failed' : 'internal-csm:organization_access_revoke_failed', options)
          )
          if (!saved.ok) return
          const isGranted = getMissingGrants(saved.result.stored, type, [item.id]).length === 0
          if (isGranted !== granted) {
            notify(t(granted ? 'internal-csm:organization_access_not_granted' : 'internal-csm:organization_access_not_revoked', options), 'error')
          } else {
            notify(t(granted ? 'internal-csm:organization_access_granted' : 'internal-csm:organization_access_revoked', options), 'success')
          }
        } finally {
          setPendingIds((current) => {
            const next = new Set(current)
            next.delete(item.id)
            return next
          })
        }
      })
    },
    [enqueue, failureMessages, notify, organization.id, organization.name, run, setAccess, t, type]
  )

  const grantAllListed = useCallback(
    async (itemIds: string[]) => {
      const options = { count: itemIds.length, organization: organization.name }
      setIsGrantingAll(true)
      await enqueue(async () => {
        try {
          const saved = await run(
            grantAll({ organizationId: organization.id, type, itemIds }).unwrap(),
            failureMessages('internal-csm:organization_access_grant_all_failed', options)
          )
          if (!saved.ok) return
          const missing = getMissingGrants(saved.result.stored, type, itemIds).length
          if (missing) notify(t('internal-csm:organization_access_not_granted_all', { ...options, missing }), 'error')
          else notify(t('internal-csm:organization_access_granted_all', options), 'success')
        } finally {
          setIsGrantingAll(false)
        }
      })
    },
    [enqueue, failureMessages, grantAll, notify, organization.id, organization.name, run, t, type]
  )

  return { pendingIds, changeAccess, grantAllListed, isGrantingAll, isBusy: isGrantingAll || pendingIds.size > 0 }
}
